import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid } from '@material-ui/core';
import { CampaignEmailDesinerIcon, CampaignRawEditorIcon, CampaignTemplatesIcon } from '@dev/ee-components';
import { useDispatch, useSelector } from 'react-redux';
import { isNil, map } from 'lodash-es';
import { AccessLevel, DefaultEditors } from '@dev/ee-api/ee-api-models';

import { GetDashboardSettings } from '@/store/dashboardSettings/actions';
import { pathsParams } from '@/services/dictionaries/Paths';
import { getAccountStore } from '@/store/account/actions';
import { getDashboardSettingsStore } from '@/store/dashboardSettings/actions';
import { ShowDialog } from '@/store/dialog/actions';
import { OpenEditorInCampaignCreator } from '@/store/raweditor/actions';
import { OpenGallery, OpenEditorDragAndDrop, OpenTemplateEditor } from '@/store/campaignCreator/actions';
import { useDashboardElementVisibility } from '@/services/hooks/useDashboardElementVisibility';
import { generateIdBasedOnPath } from '@dev/ee-tools';
import { useRouteMatch } from 'react-router';
import { ContentBox } from '../../components';
import { TemplatesGallery } from '../../components';
import { ContentBoxesConfigType } from '../../SubjectAndContentInterface';
import { SelectEditorType } from './SelectEditorTypeBody';

type TChooseContentSection = {
    openInOverlay: boolean;
};

export const ChooseContentSection = ({ openInOverlay }: TChooseContentSection): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const {
        GeneralInfo: { AccountAccessLevel },
    } = useSelector(getAccountStore);
    const { dashboardSettingsList } = useSelector(getDashboardSettingsStore);
    const { editors: { isHtmlEditorVisible } } = useDashboardElementVisibility();

    const route = useRouteMatch<{ type: string }>();

    const routeForIds =
        route?.path?.replace?.(pathsParams.name, '')?.replace(pathsParams.type, route.params?.type) ?? '';

    useEffect(() => {
        if(dashboardSettingsList) return;
        dispatch(GetDashboardSettings());
    }, []);

    const openTemplatesGallery = (): void => {
        
        dispatch(OpenGallery());
        
    };

    const openRawEditor = (): void => {
        dispatch(OpenEditorInCampaignCreator());
    };

    const openDndEditor = (): void => {
        if (
            AccountAccessLevel?.includes(AccessLevel.ViewTemplateEditor) &&
            AccountAccessLevel?.includes(AccessLevel.ViewDragDropEditor) &&
            isNil(dashboardSettingsList?.DefaultEditor)
        ) {
            const dialogParams = {
                title: t('selectdragdropeditor'),
                body: <SelectEditorType openInOverlay={openInOverlay} />,
                width: '573px',
                height: '508px',
                disableFooter: true,
            };

            dispatch(ShowDialog(dialogParams));
            return;
        } else if (dashboardSettingsList?.DefaultEditor === DefaultEditors.DragDropEditor) {
            //Classic (old) drag&drop
            dispatch(OpenEditorDragAndDrop());
            return;
        }
        //New template editor
        dispatch(OpenTemplateEditor());
    };

    const contentBoxesConfig: ContentBoxesConfigType[] = [
        {
            icon: <CampaignTemplatesIcon />,
            title: t('templates'),
            description: t('campaigntemplatesdescription'),
            onBoxClick: openTemplatesGallery,
            id: generateIdBasedOnPath('add-template-from-templates', routeForIds),
        },
        {
            icon: <CampaignEmailDesinerIcon />,
            title: t('draganddropeditor'),
            description: t('campaignddeditordescription'),
            onBoxClick: openDndEditor,
            id: generateIdBasedOnPath('add-template-template-editor', routeForIds),
        },
        ...(isHtmlEditorVisible ? [{
            icon: <CampaignRawEditorIcon />,
            title: t('htmleditor'),
            description: t('campaignhtmleditordescription'),
            onBoxClick: openRawEditor,
            id: generateIdBasedOnPath('add-template-html-editor', routeForIds),
        }] : []),
    ];


    return (
        <Grid container spacing={3} style={{ marginTop: 32 }}>
          
            {map(contentBoxesConfig, (box) => (
                <ContentBox
                    id={box.id}
                    icon={box.icon}
                    title={box.title}
                    description={box.description}
                    onBoxClick={box.onBoxClick}
                />
            ))}
            <TemplatesGallery />
         
        </Grid>
    );
};
